// Meta/Facebook pixel utilities for frontend tracking
// This works alongside the backend Meta Conversions API for improved event matching

declare global {
  interface Window {
    fbq: any;
  }
}

/**
 * Generate a unique event ID for deduplication between frontend pixel and backend CAPI
 */
export function generateEventId(): string {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Extract Facebook Browser ID (fbp) from cookies
 */
export function getFacebookBrowserId(): string | null {
  const match = document.cookie.match(/_fbp=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Extract Facebook Click ID from URL parameters (fallback for ad blockers)
 */
export function getFbclidFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('fbclid');
}

/**
 * Extract Facebook Click ID (fbc) from cookies with URL fallback
 */
export function getFacebookClickId(): string | null {
  // Try cookie first (if Meta Pixel worked)
  const match = document.cookie.match(/_fbc=([^;]+)/);
  if (match) {
    return decodeURIComponent(match[1]);
  }

  // Fallback: check URL parameter (for ad blocker users)
  const fbclid = getFbclidFromUrl();
  if (fbclid) {
    // Format it like Meta would: fb.1.timestamp.fbclid
    return `fb.1.${Date.now()}.${fbclid}`;
  }

  return null;
}

/**
 * Get Meta tracking data from browser
 */
export function getMetaTrackingData() {
  if (!isBrowser()) {
    return {
      fbp: null,
      fbc: null,
      event_id: generateEventId(),
    };
  }

  // Try to get existing event_id from sessionStorage first
  const storedEventId = window.sessionStorage.getItem('initiate_checkout_event_id');

  // fbp: library (primary, works even when Meta Pixel is blocked) → sessionStorage cache → cookie fallback
  let fbp = window.clientParamBuilder?.getFbp()
    || window.sessionStorage.getItem('meta_fbp')
    || getFacebookBrowserId();
  if (fbp) { window.sessionStorage.setItem('meta_fbp', fbp); }

  // fbc: library (primary) → sessionStorage cache → cookie/URL fallback
  let fbc = window.clientParamBuilder?.getFbc()
    || window.sessionStorage.getItem('meta_fbc')
    || getFacebookClickId();
  if (fbc) { window.sessionStorage.setItem('meta_fbc', fbc); }

  return {
    fbp,
    fbc,
    event_id: storedEventId || generateEventId(),
  };
}

/**
 * Initialize Meta Pixel (if not already done via GTM)
 */
export function initMetaPixel(pixelId: string) {
  if (typeof window.fbq !== 'undefined') {
    console.log('Meta Pixel already initialized');
    return;
  }

  // Meta Pixel Code
  (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
}

/**
 * Track Meta event with deduplication support
 */
export function trackMetaEvent(eventName: string, eventData: any = {}, eventId?: string) {
  if (typeof window.fbq === 'undefined') {
    console.warn('Meta Pixel not initialized');
    return;
  }

  if (eventId) {
    // Pass eventID as 4th parameter (options object) per Meta's format
    window.fbq('track', eventName, eventData, { eventID: eventId });
  } else {
    window.fbq('track', eventName, eventData);
  }
}

/**
 * Track InitiateCheckout event
 */
export function trackInitiateCheckout(value: number, currency: string, eventId: string) {
  trackMetaEvent('InitiateCheckout', {
    value,
    currency,
    content_type: 'product',
  }, eventId);
}

/**
 * Track Purchase event
 */
export function trackPurchase(value: number, currency: string, eventId: string, orderId?: string) {
  trackMetaEvent('Purchase', {
    value,
    currency,
    content_type: 'product',
    ...(orderId && { content_ids: [orderId] }),
  }, eventId);
}

/**
 * Check if we're in a browser environment
 */
export function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}
